import api from '../../services/Api';
import type { OverrideTemporario, TravaConfig, TravaResposta } from './travaGovernanca';
import { formatarDataHoraComFuso } from './dataHoraAuditoria';
import type { ResetOnboardingResultado } from '../clientes/justificativaReset';
import {
  AlertaStatus,
  ConfigAlcadaNivel,
  EmpresaTenant,
  GrupoEconomico,
  HistoricoSOXItem,
  ManualEntry,
  MatrizPermissao,
  MonitoramentoItem,
  MotivoDevolucao,
  PendenciaTransversal,
  PendenciasTransversaisResponse,
  SLAStatus,
  ScopeType,
  SoxEvent,
  UsuarioSistema,
  UsuarioWorkflow,
  WorkflowConciliacao,
  WorkflowStep,
} from './models';

// ─── helpers ──────────────────────────────────────────────────────────────────

function slaTexto(dataLimite: string | null, horasRestantes: number | null): string {
  if (!dataLimite) return '—';
  if (horasRestantes === null) return '—';
  if (horasRestantes < 0) return `Vencido há ${Math.abs(Math.round(horasRestantes))}h`;
  if (horasRestantes < 1) return 'Vence em menos de 1h';
  const h = Math.floor(horasRestantes);
  const m = Math.round((horasRestantes - h) * 60);
  if (h === 0) return `${m}min restantes`;
  return `${h}h ${m}min restantes`;
}

function stepStatus(backendStatus: string): WorkflowStep['status'] {
  const map: Record<string, WorkflowStep['status']> = {
    NAO_INICIADA: 'NAO_INICIADA',
    EM_ANDAMENTO: 'EM_ANDAMENTO',
    CONCLUIDA:    'CONCLUIDA',
    REJEITADA:    'REJEITADA',
    COM_ERRO:     'COM_ERRO',
  };
  return map[backendStatus] ?? 'NAO_INICIADA';
}

// ─── adapters ─────────────────────────────────────────────────────────────────

function adaptConciliacao(r: any): WorkflowConciliacao {
  return {
    id:               r.concilId,
    cartaoId:         r.cartaoId ?? null,
    empresaId:        r.tenantId,
    empresa:          r.nomeEmpresa ?? '—',
    cnpj:             r.cnpj ?? '',
    conta:            r.contaContabil ?? '',
    descricaoConta:   r.descricaoConta ?? '',
    periodo:          r.periodoReferencia ?? '',
    valorTotal:       r.valorTotal ?? 0,
    saldoContabil:    r.saldoContabil ?? 0,
    saldoConciliado:  r.saldoConciliado ?? 0,
    saldoSuporte:     r.saldoSuporte ?? r.saldoConciliado ?? 0,
    divergencia:      r.divergencia ?? 0,
    etapaAtual:       r.etapaAtualId ?? 1,
    nomeEtapa:        r.nomeEtapa ?? '',
    nivelAtual:       r.ordemEtapa ?? 1,
    responsavelNivel: r.responsavelNivel ?? null,
    responsavelAtual: r.responsavelAtualNome ?? (r.responsavelCargoNome ? `Grupo: ${r.responsavelCargoNome}` : '—'),
    responsavelCargo: r.responsavelCargoNome ?? null,
    autor:            r.autorNome ?? '—',
    autorId:          r.autorId ?? null,
    status:           r.statusConciliacao ?? 'RASCUNHO',
    risco:            r.nivelRisco ?? 'BAIXO',
    sla:              slaTexto(r.dataLimiteSla, null),
    criadoEm:         r.criadoEm ?? null,
    atualizadoEm:     r.atualizadoEm ?? null,
    hashIntegridade:  r.hashIntegridade ?? '',
    steps:            (r.steps ?? []).map((s: any): WorkflowStep => ({
      id:          s.etapaId,
      stepId:      s.nomeEtapa?.toLowerCase().replace(/\s/g, '-') ?? '',
      nome:        s.nomeEtapa ?? '',
      status:      stepStatus(s.status),
      responsavel: s.responsavelNome ?? undefined,
      cargo:       s.cargoNome ?? undefined,
      concluidoEm: s.concluidoEm ?? undefined,
      autenticador: s.autenticador ?? undefined,
      comentario:  s.comentario ?? undefined,
      erros:       s.status === 'COM_ERRO' ? 1 : 0,
      pendencias:  s.status === 'REJEITADA' ? 1 : 0,
    })),
    evidencias: (r.evidencias ?? []).map((e: any) => ({
      evidenciaId: e.evidenciaId,
      nomeArquivo: e.nomeArquivo,
      tipoMime:    e.tipoMime ?? null,
      tamanhoBytes: e.tamanhoBytes ?? null,
      enviadoEm:   e.enviadoEm ?? '',
    })),
    justificativaTecnica:  r.justificativaTecnica ?? null,
    justificativaDetalhada: r.justificativaDetalhada ?? null,
    planoAcaoCorretiva:    r.planoAcaoCorretiva ?? null,
    statusDivergencia:     r.statusDivergencia ?? 'CONCILIADO',
    justificativaObrigatoria: r.justificativaObrigatoria ?? false,
    podeEnviarAprovacao:   r.podeEnviarAprovacao ?? true,
    motivoDevolucao:       r.motivoDevolucao ?? null,
    devolvidoParaUserId:   r.devolvidoParaUserId ?? null,
    devolvidoParaNome:     r.devolvidoParaNome ?? null,
  };
}

function adaptMonitoramento(r: any): MonitoramentoItem {
  return {
    id:             r.concilId,
    conciliacaoId:  r.concilId,
    empresa:        r.nomeEmpresa ?? '—',
    grupoNome:      r.grupoNome ?? '—',
    scopeType:      (r.scopeType ?? 'GROUP') as ScopeType,
    valor:          r.valor ?? 0,
    etapa:          r.nomeEtapa ?? '—',
    nivel:          r.nivel ?? 1,
    responsavel:    r.responsavelNome ?? '—',
    responsavelEmail: r.responsavelEmail ?? undefined,
    dataLimite:     r.dataLimiteSla ? new Date(r.dataLimiteSla).toLocaleDateString('pt-BR') : '—',
    slaStatus:      (r.slaStatus ?? 'VERDE') as SLAStatus,
    dataLimiteIso:  r.dataLimiteSla ?? null,
    horasRestantes: Math.round(r.horasRestantes ?? 0),
    alertaEnviado:  r.alertaEnviado ?? false,
    escalado:       r.escalado ?? false,
  };
}

function adaptSox(r: any): SoxEvent {
  return {
    id:          r.logId,
    dataHora:    formatarDataHoraComFuso(r.timestampEvento),
    executor:    r.userExecutorNome ?? '—',
    titular:     r.userTitularNome ?? '—',
    nivel:       r.etapaId ?? 0,
    acao:        r.acaoRealizada ?? '—',
    etapa:       r.nomeEtapa ?? '—',
    ip:          r.ipOrigem ?? '—',
    userAgent:   '',
    hashAnterior: r.hashAnterior ?? '—',
    hashAtual:   r.hashSnapshot ?? '—',
    justificativa: r.justificativa ?? '',
    escopo:      r.escopo ?? '',
    modulo:      r.modulo ?? '',
  };
}

function adaptLancamento(r: any): ManualEntry {
  return {
    id:          r.lancamentoId,
    // Backend já manda formatado ("dd/MM/yyyy") — reprocessar com `new Date(...)` aqui
    // quebra, pois o construtor de Date não entende esse formato de forma confiável.
    data:        r.dataLancamento ?? '—',
    empresa:     r.nomeEmpresa ?? '—',
    empresaId:   r.tenantId ?? 0,
    conta:       `${r.contaContabil ?? ''} - ${r.historico ?? ''}`,
    cartaoId:    r.cartaoId ?? null,
    historico:   r.historico ?? '',
    valor:       r.valor ?? 0,
    autor:       r.autorNome ?? '—',
    risco:       r.riscoCompliance ?? 'MEDIO',
    status:      r.status ?? 'PENDENTE',
    justificativa: r.justificativaDetalhada ?? '',
    planoAcao:   r.planoAcaoCorretiva ?? '',
    agingDias:   r.agingDias ?? 0,
  };
}

function adaptEmpresa(r: any): EmpresaTenant {
  return {
    id:            r.tenantId ?? r.id,
    empresaId:     r.empresaId ?? undefined,
    nome:          r.nomeEmpresa || r.nomeFantasia || r.razaoSocial || r.nome || '—',
    cnpj:          r.cnpj ?? '',
    grupoId:       r.grupoId ?? null,
    responsavelId: r.responsavelId ?? null,
    status:        (r.status ?? (r.planoAtivo !== false ? 'ATIVA' : 'INATIVA')) as any,
  };
}

function adaptGrupo(r: any): GrupoEconomico {
  return {
    grupo_id:      r.grupoId,
    nome:          r.nome ?? '—',
    tenant_ids:    (r.tenants ?? []).map((t: any) => t.tenantId),
    responsavel:   r.responsavelNome ?? '—',
    responsavelId: r.responsavelId ?? (r.tenants?.[0]?.responsavelId ?? null),
    status:        r.status ?? 'ATIVO',
  };
}

function adaptUsuario(r: any): UsuarioWorkflow {
  return {
    id:         r.userId,
    authUserId: r.authUserId ?? 0,
    nome:       r.nome ?? '—',
    email:      r.email ?? '',
    cargo:      r.nomeCargo ?? r.cargoNome ?? '',
    nivel:      r.nivelHierarquico ?? 1,
    ativo:      r.ativo ?? true,
    adminTi:    r.adminTi ?? false,
    bloqueado:  r.bloqueado === true,
    empresaIds: r.empresaIds ?? [],
  };
}

function adaptPlanoMelhoriaAcao(r: any): import('./models').PlanoMelhoriaAcao {
  return {
    acaoId: r.acaoId, planoId: r.planoId,
    responsavelId: r.responsavelId ?? null, responsavelNome: r.responsavelNome ?? null,
    descricao: r.descricao ?? '', prazo: r.prazo ?? null,
    status: r.status ?? 'NAO_INICIADO', dataConclusao: r.dataConclusao ?? null,
    evidencia: r.evidencia ?? null, observacao: r.observacao ?? null,
    pilar: r.pilar ?? 'PROCESSOS', fase: r.fase ?? 'PLAN',
  };
}

function adaptPlanoMelhoria(r: any): import('./models').PlanoMelhoria {
  return {
    planoId: r.planoId, concilId: r.concilId,
    templateId: r.templateId ?? null, tituloTemplate: r.tituloTemplate ?? null,
    justificativaDetalhada: r.justificativaDetalhada ?? '',
    pdca: typeof r.pdcaJson === 'string' ? JSON.parse(r.pdcaJson) : r.pdcaJson,
    status: r.status ?? 'NAO_INICIADO',
    checkValidado: r.checkValidado ?? false, checkValidadoPorNome: r.checkValidadoPorNome ?? null,
    checkValidadoEm: r.checkValidadoEm ?? null, checkObservacao: r.checkObservacao ?? null,
    atoRegistrado: r.atoRegistrado ?? false, atoTexto: r.atoTexto ?? null,
    atoRegistradoPorNome: r.atoRegistradoPorNome ?? null, atoRegistradoEm: r.atoRegistradoEm ?? null,
    podeEncerrar: r.podeEncerrar ?? false,
    acoes: Array.isArray(r.acoes) ? r.acoes.map(adaptPlanoMelhoriaAcao) : [],
  };
}

function adaptAlcada(r: any): ConfigAlcadaNivel {
  return {
    id:         r.configId,
    etapaId:    r.etapaId,
    cargoId:    r.cargoId,
    nivel:      r.nivelHierarquico ?? r.cargoNivel ?? 1,
    cargo:      r.nomeCargo ?? r.cargoNome ?? '—',
    scopeType:  (r.scopeType ?? 'GROUP') as ScopeType,
    scopeLabel: r.scopeLabel ?? '',
    scopeTenantIds: Array.isArray(r.scopeTenantIds) ? r.scopeTenantIds : [],
    valorMin:   r.valorMinimo ?? 0,
    valorMax:   r.valorMaximo ?? null,
    slaDiasUteis: r.slaDiasUteis ?? 1,
    titularId:  r.titularId ?? null,
    titular:    r.titularNome ?? '—',
    substitutoId: r.substitutoId ?? null,
    substituto: r.substitutoNome ?? '—',
    status:     (r.status ?? 'OK') as AlertaStatus,
    alertas:    Array.isArray(r.alertas) ? r.alertas : [],
  };
}

// ─── adapters (continuação) ────────────────────────────────────────────────────

function adaptMatrizPermissao(r: any): MatrizPermissao {
  return {
    id:              r.acessoId ?? r.id,
    usuarioId:       r.userId ?? r.usuarioId,
    usuarioNome:     r.userNome ?? r.usuarioNome ?? '—',
    empresaId:       r.tenantId ?? r.empresaId,
    empresaNome:     r.tenantNome ?? r.empresaNome ?? '—',
    concedidoPor:    r.concedidoPorNome ?? r.concedidoPor ?? '—',
    dataConcessao:   r.dataCriacao ? new Date(r.dataCriacao).toLocaleString('pt-BR') : '—',
    justificativa:   r.justificativa ?? '',
  };
}

// ─── service ──────────────────────────────────────────────────────────────────

const BASE = 'workflow';

const workflowService = {

  // Conciliações
  async listarConciliacoes(status?: string, offset = 0, perPage = 50): Promise<WorkflowConciliacao[]> {
    const { data } = await api.get(`${BASE}/conciliacoes`, { params: { status, offset, perPage } });
    return (Array.isArray(data) ? data : data?.itens ?? []).map(adaptConciliacao);
  },

  // Aprovações registradas por nível (N1–N7) para o funil do dashboard.
  async aprovacoesPorNivel(empresaId?: number, periodo?: string): Promise<{ niveis: { nivel: number; aprovacoes: number }[]; total: number }> {
    const { data } = await api.get(`${BASE}/conciliacoes/aprovacoes-por-nivel`, {
      params: { empresaId: empresaId || undefined, periodo: periodo || undefined },
    });
    return { niveis: Array.isArray(data?.niveis) ? data.niveis : [], total: data?.total ?? 0 };
  },

  async listarMinhasPendencias(): Promise<WorkflowConciliacao[]> {
    const { data } = await api.get(`${BASE}/conciliacoes/minhas-pendencias`);
    return (Array.isArray(data) ? data : data?.itens ?? []).map(adaptConciliacao);
  },

  async obterConciliacao(id: number): Promise<WorkflowConciliacao | null> {
    const { data } = await api.get(`${BASE}/conciliacoes/${id}`);
    return data ? adaptConciliacao(data) : null;
  },

  async criarConciliacao(dto: {
    tenantId: number;
    contaContabil?: string;
    descricaoConta?: string;
    periodoReferencia?: string;
    valorTotal?: number;
    nivelRisco?: string;
  }): Promise<WorkflowConciliacao> {
    const { data } = await api.post(`${BASE}/conciliacoes`, dto);
    return adaptConciliacao(data);
  },

  async aprovarConciliacao(id: number, justificativa?: string): Promise<WorkflowConciliacao> {
    const { data } = await api.post(`${BASE}/conciliacoes/${id}/aprovar`, { justificativa });
    return adaptConciliacao(data);
  },

  /** Governança de TI: prorroga o prazo, bloqueia ou devolve ao autor uma conta vencida (TI ou N5+). */
  async alterarStatusContaVencida(id: number, corpo: { acao: string; justificativa: string; novoPrazo?: string }): Promise<WorkflowConciliacao> {
    const { data } = await api.post(`${BASE}/conciliacoes/${id}/conta-vencida`, corpo);
    return adaptConciliacao(data);
  },

  async rejeitarConciliacao(id: number, motivo: string): Promise<WorkflowConciliacao> {
    const { data } = await api.post(`${BASE}/conciliacoes/${id}/rejeitar`, { motivo });
    return adaptConciliacao(data);
  },

  // Pendências Transversais (transbordo entre ciclos)
  async listarPendenciasTransversais(tenantId?: number, periodo?: string): Promise<PendenciasTransversaisResponse> {
    const { data } = await api.get(`${BASE}/conciliacoes/pendencias-transversais`, { params: { tenantId: tenantId || undefined, periodo: periodo || undefined } });
    return {
      dividaTecnicaAtual: data?.dividaTecnicaAtual ?? 0,
      abertas:            data?.abertas ?? 0,
      herdadas:           data?.herdadas ?? 0,
      vencidas:           data?.vencidas ?? 0,
      agingMedioDias:     data?.agingMedioDias ?? 0,
      itens: (data?.itens ?? []).map((i: any): PendenciaTransversal => ({
        concilId:       i.concilId,
        empresa:        i.empresa ?? '—',
        conta:          i.contaContabil ?? '',
        descricaoConta: i.descricaoConta ?? '',
        cicloOriginal:  i.cicloOriginal ?? '',
        cicloAtual:     i.cicloAtual ?? '',
        problema:       i.problema ?? '',
        prazo:          i.prazo ?? null,
        agingDias:      i.agingDias ?? 0,
        status:         i.status ?? 'EM_TRATAMENTO',
        herdada:        !!i.herdada,
      })),
    };
  },

  // Monitoramento
  async listarMonitoramento(tenantId?: number, periodo?: string): Promise<{ itens: MonitoramentoItem[]; verde: number; amarelo: number; vermelho: number }> {
    const { data } = await api.get(`${BASE}/monitoramento`, { params: { tenantId: tenantId || undefined, periodo: periodo || undefined } });
    return {
      itens:    (data?.itens ?? []).map(adaptMonitoramento),
      verde:    data?.totalVerde ?? 0,
      amarelo:  data?.totalAmarelo ?? 0,
      vermelho: data?.totalVermelho ?? 0,
    };
  },

  // Auditoria SOX
  async listarSox(offset = 0, perPage = 50): Promise<SoxEvent[]> {
    const { data } = await api.get(`${BASE}/auditoria/sox`, { params: { offset, perPage } });
    return (Array.isArray(data) ? data : data?.itens ?? []).map(adaptSox);
  },

  // Lançamentos Manuais
  async listarLancamentos(tenantId?: number, esteiraId?: number, offset = 0, perPage = 50): Promise<ManualEntry[]> {
    const { data } = await api.get(`${BASE}/lancamentos`, { params: { tenantId: tenantId || undefined, esteiraId: esteiraId || undefined, offset, perPage } });
    return (Array.isArray(data) ? data : data?.itens ?? []).map(adaptLancamento);
  },

  /** Exporta (CSV) os lançamentos manuais; sem filtro, todos os que o usuário pode ver. */
  async exportarLancamentos(tenantId?: number, esteiraId?: number): Promise<Blob> {
    const { data } = await api.get(`${BASE}/lancamentos/exportar`, {
      params: { tenantId: tenantId || undefined, esteiraId: esteiraId || undefined },
      responseType: 'blob',
    });
    return data as Blob;
  },

  async criarLancamento(dto: {
    tenantId: number;
    esteiraId: number;
    dataLancamento: string;
    contaContabil: string;
    historico: string;
    valor: number;
    tipo: 'CREDITO' | 'DEBITO';
    riscoCompliance: 'BAIXO' | 'MEDIO' | 'ALTO';
    justificativaDetalhada: string;
    planoAcaoCorretiva: string;
  }): Promise<ManualEntry> {
    const { data } = await api.post(`${BASE}/lancamentos`, dto);
    return adaptLancamento(data);
  },

  async aprovarLancamento(id: number, justificativa?: string): Promise<ManualEntry> {
    const { data } = await api.post(`${BASE}/lancamentos/${id}/aprovar`, { justificativa });
    return adaptLancamento(data);
  },

  async rejeitarLancamento(id: number, motivo: string): Promise<ManualEntry> {
    const { data } = await api.post(`${BASE}/lancamentos/${id}/rejeitar`, { motivo });
    return adaptLancamento(data);
  },

  async listarTenants(): Promise<EmpresaTenant[]> {
    const { data } = await api.get(`${BASE}/tenants`);
    return (Array.isArray(data) ? data : []).map(adaptEmpresa);
  },

  // Grupos Econômicos
  async listarGrupos(): Promise<GrupoEconomico[]> {
    const { data } = await api.get(`${BASE}/grupos`);
    return (Array.isArray(data) ? data : []).map(adaptGrupo);
  },

  async criarGrupo(dto: { nome: string; status?: string; justificativaSox?: string }): Promise<GrupoEconomico> {
    const { data } = await api.post(`${BASE}/grupos`, dto);
    return adaptGrupo(data);
  },

  async atualizarGrupo(grupoId: number, dto: { nome: string; status: string; justificativaSox?: string }): Promise<GrupoEconomico> {
    const { data } = await api.put(`${BASE}/grupos/${grupoId}`, dto);
    return adaptGrupo(data);
  },

  async vincularTenantGrupo(grupoId: number, tenantId: number, justificativa: string): Promise<void> {
    await api.post(`${BASE}/grupos/${grupoId}/vincular`, { tenantId, justificativaSox: justificativa });
  },

  // Matriz de Acessos
  async listarAcessos(): Promise<MatrizPermissao[]> {
    const { data } = await api.get(`${BASE}/acessos`);
    return (Array.isArray(data) ? data : []).map(adaptMatrizPermissao);
  },

  async concederAcesso(dto: { userIds: number[]; empresaIds: number[]; justificativa: string }): Promise<void> {
    await api.post(`${BASE}/acessos`, dto);
  },

  async revogarAcesso(usuarioId: number, empresaId: number): Promise<void> {
    await api.delete(`${BASE}/acessos/${usuarioId}/${empresaId}`);
  },

  async listarEmpresasOrfas(): Promise<EmpresaTenant[]> {
    const { data } = await api.get(`${BASE}/grupos/orfas`);
    return (Array.isArray(data) ? data : []).map(adaptEmpresa);
  },

  // Usuários do sistema principal filtrados pelo clienteID do usuário logado
  async listarUsuariosSistema(clienteId: number): Promise<UsuarioSistema[]> {
    const { data } = await api.get(`Usuario/BuscarPorClienteID/${clienteId}`);
    return (Array.isArray(data) ? data : []).map((r: any): UsuarioSistema => ({
      id:          r.id,
      nome:        r.nome ?? '—',
      email:       r.email ?? '',
      nomeUsuario: r.nomeUsuario ?? '',
      perfil:      r.perfil ?? '',
      ativo:       r.ativo !== false,
      bloqueado:   r.bloqueado === true,
    }));
  },

  // Atribui ou atualiza nível workflow de um usuário existente
  async atribuirNivel(dto: {
    authUserId: number;
    nome: string;
    email: string;
    nivelHierarquico: number;
    nomeCargo: string;
  }): Promise<UsuarioWorkflow> {
    const { data } = await api.post(`${BASE}/usuarios/atribuir-nivel`, dto);
    return adaptUsuario(data);
  },

  // Administrador TI — permissão de configuração do módulo, independente do nível
  // hierárquico de aprovação (N1-N7).
  async setAdminTi(dto: { authUserId: number; nome: string; email: string; ativo: boolean }): Promise<UsuarioWorkflow> {
    const { data } = await api.post(`${BASE}/usuarios/admin-ti`, dto);
    return adaptUsuario(data);
  },

  // Usuários WF
  async getMeuPerfil(): Promise<UsuarioWorkflow | null> {
    try {
      const { data } = await api.get(`${BASE}/usuarios/me`);
      return adaptUsuario(data);
    } catch {
      return null;
    }
  },

  async listarUsuarios(): Promise<UsuarioWorkflow[]> {
    const { data } = await api.get(`${BASE}/usuarios`);
    return (Array.isArray(data) ? data : []).map(adaptUsuario);
  },

  async criarUsuario(dto: {
    authUserId: number;
    tenantId: number;
    nome: string;
    email: string;
    cargoId: number;
  }): Promise<UsuarioWorkflow> {
    const { data } = await api.post(`${BASE}/usuarios`, dto);
    return adaptUsuario(data);
  },

  async atualizarUsuario(id: number, dto: { cargoId?: number; tenantId?: number; ativo?: boolean }): Promise<UsuarioWorkflow> {
    const { data } = await api.put(`${BASE}/usuarios/${id}`, dto);
    return adaptUsuario(data);
  },

  async desativarUsuario(id: number): Promise<void> {
    await api.delete(`${BASE}/usuarios/${id}`);
  },

  // Config — Alçadas
  async listarAlcadas(): Promise<ConfigAlcadaNivel[]> {
    const { data } = await api.get(`${BASE}/config/alcadas`);
    return (Array.isArray(data) ? data : []).map(adaptAlcada);
  },

  async salvarAlcada(dto: {
    etapaId: number;
    cargoId: number;
    scopeType: string;
    scopeTenantIds?: number[];
    valorMinimo?: number;
    valorMaximo?: number;
    slaDiasUteis: number;
    titularId?: number;
    substitutoId?: number;
    justificativaSox?: string;
  }): Promise<void> {
    await api.post(`${BASE}/config/alcadas`, dto);
  },

  async atualizarAlcada(configId: number, dto: {
    etapaId: number;
    cargoId: number;
    scopeType: string;
    scopeTenantIds?: number[];
    valorMinimo?: number;
    valorMaximo?: number;
    slaDiasUteis: number;
    titularId?: number;
    substitutoId?: number;
    justificativaSox?: string;
  }): Promise<void> {
    await api.put(`${BASE}/config/alcadas/${configId}`, dto);
  },

  async excluirAlcada(configId: number): Promise<void> {
    await api.delete(`${BASE}/config/alcadas/${configId}`);
  },

  // Config — Cargos
  async listarCargos(): Promise<{ cargoId: number; nomeCargo: string; nivelHierarquico: number; funcaoDescricao?: string; permissoes?: string[]; ativo: boolean }[]> {
    const { data } = await api.get(`${BASE}/config/cargos`);
    return Array.isArray(data) ? data : [];
  },

  async criarCargo(dto: { nomeCargo: string; nivelHierarquico: number; funcaoDescricao?: string; permissoes?: string[] }): Promise<void> {
    await api.post(`${BASE}/config/cargos`, dto);
  },

  async atualizarCargo(id: number, dto: { nomeCargo: string; funcaoDescricao?: string; permissoes?: string[] }): Promise<void> {
    await api.put(`${BASE}/config/cargos/${id}`, dto);
  },

  async excluirCargo(id: number): Promise<void> {
    await api.delete(`${BASE}/config/cargos/${id}`);
  },

  // Config — Matriz de SLA do Fechamento (D+5)
  async listarEtapasSla(): Promise<{ etapaId: number; nomeEtapa: string; ordemSequencial: number; descricao?: string; slaDiasUteis: number | null }[]> {
    const { data } = await api.get(`${BASE}/config/etapas`);
    return Array.isArray(data) ? data : [];
  },

  async atualizarSlaEtapa(etapaId: number, slaDiasUteis: number, justificativaSox?: string): Promise<void> {
    await api.put(`${BASE}/config/etapas/${etapaId}`, { slaDiasUteis, justificativaSox });
  },

  // Governança de TI: políticas de trava e exceções temporárias (dados reais, com registro SOX)
  async obterTrava(): Promise<TravaResposta> {
    const { data } = await api.get(`${BASE}/config/trava`);
    return data;
  },

  async salvarTrava(body: TravaConfig & { justificativaSox: string }): Promise<TravaResposta> {
    const { data } = await api.put(`${BASE}/config/trava`, body);
    return data;
  },

  async listarOverrides(): Promise<OverrideTemporario[]> {
    const { data } = await api.get(`${BASE}/config/overrides`);
    return Array.isArray(data) ? data : [];
  },

  async criarOverride(body: { empresaId: number; motivo: string; validadeHoras: number }): Promise<{ overrideId: number; expiraEm: string }> {
    const { data } = await api.post(`${BASE}/config/overrides`, body);
    return data;
  },

  async revogarOverride(id: number, justificativaSox: string): Promise<void> {
    await api.post(`${BASE}/config/overrides/${id}/revogar`, { justificativaSox });
  },

  // Integridade das alçadas
  async verificarIntegridadeAlcadas(): Promise<any[]> {
    const { data } = await api.get(`${BASE}/config/alcadas/integridade`);
    return Array.isArray(data) ? data : [];
  },

  // Envio manual de alerta de SLA de uma conciliação (destinatário, e-mail, assunto e mensagem editáveis)
  async enviarAlertaManual(concilId: number, body: { tipo: 'A' | 'B'; destinatarioNome?: string; destinatarioEmail: string; assunto?: string; mensagem?: string }): Promise<{ enviado: boolean; destinatario: string }> {
    const { data } = await api.post(`${BASE}/monitoramento/conciliacoes/${concilId}/enviar-alerta`, body);
    return data;
  },

  // Disparar alertas de SLA
  async dispararAlertas(): Promise<{ alertasDisparados: number; escalamentos: number }> {
    const { data } = await api.post(`${BASE}/monitoramento/disparar-alertas`, {});
    return data;
  },

  // SOX com filtros
  async listarSoxFiltrado(params: { periodo?: string; grupoId?: number; executor?: string; concilId?: number; offset?: number; perPage?: number } = {}): Promise<any[]> {
    const { data } = await api.get(`${BASE}/auditoria/sox`, { params });
    return (Array.isArray(data) ? data : data?.itens ?? []);
  },

  // Log de rastreio SOX de uma conciliação específica (do ECD/entrada no Workflow até a
  // aprovação atual) — mesma fonte de dados da Auditoria SOX geral, só filtrada por concilId.
  async listarHistoricoConciliacao(concilId: number): Promise<SoxEvent[]> {
    const { data } = await api.get(`${BASE}/auditoria/sox`, { params: { concilId, perPage: 200 } });
    return (Array.isArray(data) ? data : data?.itens ?? []).map(adaptSox);
  },

  // Solicitar Complemento — devolução hierárquica ao aprovador do módulo de Conciliação
  async solicitarComplemento(id: number, dto: { motivoDevolucao: MotivoDevolucao; observacao?: string }): Promise<WorkflowConciliacao> {
    const { data } = await api.post(`${BASE}/conciliacoes/${id}/solicitar-complemento`, dto);
    return adaptConciliacao(data);
  },

  // Sinaliza que o complemento pedido foi atendido — reenvia pro responsável real da etapa
  // atual (não avança etapa, distinto de aprovarConciliacao).
  async resolverComplemento(id: number, observacao?: string): Promise<WorkflowConciliacao> {
    const { data } = await api.post(`${BASE}/conciliacoes/${id}/resolver-complemento`, { observacao });
    return adaptConciliacao(data);
  },

  // Justificativa técnica da conciliação
  async salvarJustificativa(id: number, justificativaTecnica: string): Promise<void> {
    await api.put(`${BASE}/conciliacoes/${id}/justificativa`, { justificativaTecnica });
  },

  // Catálogo de justificativa + plano de melhoria PDCA pré-cadastrados (seção 16)
  async listarJustificativaTemplates(): Promise<import('./models').JustificativaTemplate[]> {
    const { data } = await api.get(`${BASE}/conciliacoes/justificativa-templates`);
    return (Array.isArray(data) ? data : []).map((r: any): import('./models').JustificativaTemplate => ({
      templateId: r.templateId,
      titulo: r.titulo ?? '',
      justificativaDetalhada: r.justificativaDetalhada ?? '',
      pdca: typeof r.pdcaJson === 'string' ? JSON.parse(r.pdcaJson) : r.pdcaJson,
    }));
  },

  async criarJustificativaTemplate(dto: { titulo: string; justificativaDetalhada: string; pdca: import('./models').PdcaConteudo }): Promise<import('./models').JustificativaTemplate> {
    const { data } = await api.post(`${BASE}/conciliacoes/justificativa-templates`, { titulo: dto.titulo, justificativaDetalhada: dto.justificativaDetalhada, ...dto.pdca });
    return { templateId: data.templateId, titulo: data.titulo ?? '', justificativaDetalhada: data.justificativaDetalhada ?? '', pdca: typeof data.pdcaJson === 'string' ? JSON.parse(data.pdcaJson) : data.pdcaJson };
  },

  async atualizarJustificativaTemplate(templateId: number, dto: { titulo: string; justificativaDetalhada: string; pdca: import('./models').PdcaConteudo }): Promise<import('./models').JustificativaTemplate> {
    const { data } = await api.put(`${BASE}/conciliacoes/justificativa-templates/${templateId}`, { titulo: dto.titulo, justificativaDetalhada: dto.justificativaDetalhada, ...dto.pdca });
    return { templateId: data.templateId, titulo: data.titulo ?? '', justificativaDetalhada: data.justificativaDetalhada ?? '', pdca: typeof data.pdcaJson === 'string' ? JSON.parse(data.pdcaJson) : data.pdcaJson };
  },

  async excluirJustificativaTemplate(templateId: number): Promise<void> {
    await api.delete(`${BASE}/conciliacoes/justificativa-templates/${templateId}`);
  },

  // ─── Plano de Melhoria PDCA ──────────────────────────────────────────────────

  async obterPlanoMelhoria(concilId: number): Promise<import('./models').PlanoMelhoria | null> {
    try {
      const { data } = await api.get(`workflow/plano-melhoria`, { params: { concilId } });
      return adaptPlanoMelhoria(data);
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async criarPlanoMelhoria(dto: { concilId: number; templateId?: number; justificativaDetalhada?: string; pdcaJson?: string }): Promise<import('./models').PlanoMelhoria> {
    const { data } = await api.post(`workflow/plano-melhoria`, dto);
    return adaptPlanoMelhoria(data);
  },

  async atualizarPlanoMelhoria(planoId: number, dto: { justificativaDetalhada?: string; pdcaJson?: string; status?: string }): Promise<import('./models').PlanoMelhoria> {
    const { data } = await api.put(`workflow/plano-melhoria/${planoId}`, dto);
    return adaptPlanoMelhoria(data);
  },

  async registrarCheckPlanoMelhoria(planoId: number, validado: boolean, observacao?: string): Promise<import('./models').PlanoMelhoria> {
    const { data } = await api.post(`workflow/plano-melhoria/${planoId}/check`, { validado, observacao });
    return adaptPlanoMelhoria(data);
  },

  async registrarAtoPlanoMelhoria(planoId: number, texto: string): Promise<import('./models').PlanoMelhoria> {
    const { data } = await api.post(`workflow/plano-melhoria/${planoId}/ato`, { texto });
    return adaptPlanoMelhoria(data);
  },

  async encerrarPlanoMelhoria(planoId: number): Promise<import('./models').PlanoMelhoria> {
    const { data } = await api.post(`workflow/plano-melhoria/${planoId}/encerrar`);
    return adaptPlanoMelhoria(data);
  },

  async adicionarAcaoPlanoMelhoria(planoId: number, dto: import('./models').PlanoMelhoriaAcaoInput): Promise<import('./models').PlanoMelhoriaAcao> {
    const { data } = await api.post(`workflow/plano-melhoria/${planoId}/acoes`, dto);
    return adaptPlanoMelhoriaAcao(data);
  },

  async atualizarAcaoPlanoMelhoria(acaoId: number, dto: import('./models').PlanoMelhoriaAcaoInput): Promise<import('./models').PlanoMelhoriaAcao> {
    const { data } = await api.put(`workflow/plano-melhoria/acoes/${acaoId}`, dto);
    return adaptPlanoMelhoriaAcao(data);
  },

  // Upload de evidência
  async uploadEvidencia(id: number, arquivo: File, descricao: string): Promise<void> {
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    fd.append('descricao', descricao);
    await api.post(`${BASE}/conciliacoes/${id}/evidencias`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Export conciliação
  // Capa SOX da conciliação como página HTML (mesmo modelo do PDF) — aberta numa nova aba para imprimir.
  async capaHtmlConciliacao(id: number): Promise<string> {
    const { data } = await api.get(`${BASE}/conciliacoes/${id}/capa-html`, { responseType: 'text' });
    return String(data);
  },

  // finalidade 'impressao' só diferencia o registro no log SOX (IMPRESSÃO DE CAPA × EXPORTAÇÃO DE CAPA).
  async exportarConciliacao(id: number, formato: 'pdf' | 'excel', finalidade?: 'impressao'): Promise<Blob> {
    const { data } = await api.get(`${BASE}/conciliacoes/${id}/exportar`, {
      params: { formato, ...(finalidade ? { finalidade } : {}) },
      responseType: 'blob',
    });
    return data as Blob;
  },

  // ─── Onboarding de Governança ────────────────────────────────────────────────

  async onboardingStatus(tenantId: number): Promise<{
    tenantId: number | null;
    etapa1Concluida: boolean; etapa2Concluida: boolean;
    etapa3Concluida: boolean; etapa4Concluida: boolean; etapa5Concluida: boolean;
    goLiveAtivo: boolean; goLiveEm: string | null;
    etapasConcluidas: number; progressoPercent: number;
    bloqueios: { etapa: number; titulo: string; motivo: string }[];
  }> {
    const { data } = await api.get(`${BASE}/onboarding/status`, { params: { tenantId } });
    return data;
  },

  async concluirEtapaOnboarding(dto: { tenantId: number; etapa: number; justificativaSox: string }): Promise<void> {
    await api.post(`${BASE}/onboarding/etapa`, dto);
  },

  async validarOnboarding(tenantId: number): Promise<{ pronto: boolean; status: any }> {
    const { data } = await api.get(`${BASE}/onboarding/validar`, { params: { tenantId } });
    return data;
  },

  async onboardingStatusBatch(tenantIds: number[]): Promise<Array<{
    tenantId: number; goLiveAtivo: boolean; progressoPercent: number; etapasConcluidas: number;
  }>> {
    if (tenantIds.length === 0) return [];
    const { data } = await api.get(`${BASE}/onboarding/status-batch`, {
      params: { tenantIds: tenantIds.join(',') },
    });
    return data;
  },

  /** Reset do Workflow de um cliente (perfil Master). Devolve o que foi de fato alterado. */
  async resetOnboarding(clienteId: number, justificativaSox: string): Promise<ResetOnboardingResultado | undefined> {
    const { data } = await api.post(`${BASE}/onboarding/${clienteId}/reset`, { justificativaSox });
    return data?.resultado;
  },

  // ─── Calendário Fiscal / BrasilAPI ──────────────────────────────────────────

  async listarFeriados(ano?: number): Promise<{ ano: number; total: number; feriados: { data: string; descricao: string; tipo: string }[] }> {
    const { data } = await api.get(`${BASE}/calendario/feriados`, { params: { ano: ano ?? new Date().getFullYear() } });
    return data;
  },

  async sincronizarCalendario(ano: number, justificativaSox: string, forcar = false): Promise<{ ano: number; feriadosImportados: number; feriados: any[]; jaSincronizado?: boolean; mensagem?: string }> {
    const { data } = await api.post(`${BASE}/calendario/sincronizar`, { ano, justificativaSox, forcar });
    return data;
  },

  async calcularSla(dataInicio: string, diasUteis: number): Promise<{
    dataInicio: string; dataFim: string;
    diasUteisContados: number; feriadosExcluidos: number; finsDeSemanaExcluidos: number;
  }> {
    const { data } = await api.post(`${BASE}/calendario/calcular-sla`, { dataInicio, diasUteis });
    return data;
  },

  // ─── Matriz de Responsabilidades SOX ────────────────────────────────────────

  async matrizArvore(): Promise<import('./models').MatrizArvoreNo[]> {
    const { data } = await api.get(`${BASE}/matriz/arvore`);
    return Array.isArray(data) ? data.map((r: any): import('./models').MatrizArvoreNo => ({
      id: r.id, nome: r.nome, tipo: r.tipo, paiId: r.paiId ?? null,
      tenantId: r.tenantId ?? null, empresaId: r.empresaId ?? null,
      cnpj: r.cnpj ?? null, filhosCount: r.filhosCount ?? 0,
    })) : [];
  },

  // excluirResultado: omite contas de resultado (referencial "3.*") — usado pelo De/Para.
  async matrizContas(tenantId: number, busca?: string, excluirResultado = false): Promise<import('./models').MatrizContaContabil[]> {
    const { data } = await api.get(`${BASE}/matriz/contas`, {
      params: { tenantId, busca, ...(excluirResultado ? { excluirResultado: true } : {}) },
    });
    return Array.isArray(data) ? data.map((r: any): import('./models').MatrizContaContabil => ({
      contaId: r.contaId, tenantId: r.tenantId, codigo: r.codigo ?? '',
      codigoReduzido: r.codigoReduzido ?? '', descricao: r.descricao ?? '',
      tipo: r.tipo ?? 'ANALITICA', nivel: r.nivel ?? 1,
      grupoConta: r.grupoConta ?? null, contaPaiCodigo: r.contaPaiCodigo ?? null,
      bloqueada: r.bloqueada ?? false, aprovadorUserId: r.aprovadorUserId ?? null,
      aprovadorNome: r.aprovadorNome ?? null, cargoId: r.cargoId ?? null, nomeCargo: r.nomeCargo ?? null,
      statusVinculo: r.statusVinculo ?? 'PENDENTE',
    })) : [];
  },

  async matrizVincular(dto: {
    tenantId: number; contaIds: number[]; aprovadorUserId?: number; cargoId?: number; usuarioLogadoId: number; justificativaSox?: string;
  }): Promise<{ vinculadas: number; bloqueadas: number; erros: string[] }> {
    const { data } = await api.post(`${BASE}/matriz/vincular`, dto);
    return data;
  },

  async matrizGruposContas(tenantId: number): Promise<string[]> {
    const { data } = await api.get(`${BASE}/matriz/grupos`, { params: { tenantId } });
    return Array.isArray(data) ? data : [];
  },

  async matrizUsuariosAptos(tenantId: number, contaId?: number): Promise<import('./models').MatrizUsuarioApto[]> {
    const { data } = await api.get(`${BASE}/matriz/usuarios-aptos`, { params: { tenantId, contaId: contaId ?? 0 } });
    return Array.isArray(data) ? data.map((r: any): import('./models').MatrizUsuarioApto => ({
      userId: r.userId, nome: r.nome ?? '—', email: r.email ?? '',
      cargo: r.cargo ?? '—', nivelHierarquico: r.nivelHierarquico ?? 0,
      aptoSod: r.aptoSod ?? false, motivoInaptidao: r.motivoInaptidao ?? null,
    })) : [];
  },

  async matrizLog(tenantId: number): Promise<import('./models').MatrizLogEntry[]> {
    const { data } = await api.get(`${BASE}/matriz/log`, { params: { tenantId } });
    return Array.isArray(data) ? data.map((r: any): import('./models').MatrizLogEntry => ({
      logId: r.logId, tenantId: r.tenantId, contaId: r.contaId,
      contaCodigo: r.contaCodigo ?? '', aprovadorAnteriorNome: r.aprovadorAnteriorNome ?? null,
      aprovadorNovoNome: r.aprovadorNovoNome ?? '—', usuarioLogadoNome: r.usuarioLogadoNome ?? '—',
      acaoEm: r.acaoEm, hashValidacao: r.hashValidacao ?? '',
      acao: r.acao ?? 'VINCULAR', justificativaSox: r.justificativaSox ?? null,
    })) : [];
  },

  async matrizImportar(dto: {
    tenantId: number; periodoReferencia: string;
    contas: { codigo: string; codigoReduzido?: string; descricao: string; tipo?: string; nivel?: number; grupoConta?: string; contaPaiCodigo?: string }[];
  }): Promise<{ importadas: number; atualizadas: number }> {
    const { data } = await api.post(`${BASE}/matriz/importar`, dto);
    return data;
  },

  // ─── Dashboard de Integridade (Governança SOX) ──────────────────────────────

  async matrizCobertura(tenantId: number): Promise<{
    tenantId: number; nomeEmpresa: string; totalContas: number;
    contasVinculadas: number; contasPendentes: number; percentualCobertura: number;
    statusCobertura: 'VERMELHO' | 'AMARELO' | 'VERDE'; podeAtivarWorkflow: boolean;
    pendencias: {
      contaId: number; tenantId: number; nomeEmpresa: string; grupoEconomico: string;
      codigo: string; codigoReduzido: string; descricao: string;
      grupoConta: string | null; nivel: number; tipo: string;
    }[];
  }> {
    const { data } = await api.get(`${BASE}/matriz/cobertura`, { params: { tenantId } });
    return data;
  },

  async matrizExportarPendencias(tenantId: number): Promise<Blob> {
    const { data } = await api.get(`${BASE}/matriz/pendencias/exportar`, {
      params: { tenantId },
      responseType: 'blob',
    });
    return data as Blob;
  },

  async matrizNotificarPendencias(tenantId: number): Promise<{ notificados: number; pendencias: number; cobertura: number }> {
    const { data } = await api.post(`${BASE}/matriz/notificar-pendencias`, null, { params: { tenantId } });
    return data;
  },

  async matrizCoberturaGlobal(): Promise<{
    totalEmpresas: number;
    empresasVerde: number;
    empresasAmarelo: number;
    empresasVermelho: number;
    totalContasGlobal: number;
    contasVinculadasGlobal: number;
    percentualGlobal: number;
    goLiveBloqueado: boolean;
    empresas: {
      tenantId: number; nomeEmpresa: string; totalContas: number;
      contasVinculadas: number; contasPendentes: number; percentualCobertura: number;
      statusCobertura: 'VERMELHO' | 'AMARELO' | 'VERDE'; podeAtivarWorkflow: boolean;
      pendencias: any[];
    }[];
  }> {
    const { data } = await api.get(`${BASE}/matriz/cobertura/global`);
    return data;
  },

  // ─── De/Para — Hierarquia de Grupos de Contas ────────────────────────────────

  async deParaListar(tenantId: number): Promise<{
    grupoContaId: number; tenantId: number; grupoNome: string;
    subgrupoNome: string | null; contaId: number; contaCodigo: string;
    contaDescricao: string; criadoEm: string; origem?: 'USUARIO' | 'REGRA';
  }[]> {
    const { data } = await api.get(`${BASE}/depara`, { params: { tenantId } });
    return Array.isArray(data) ? data : [];
  },

  async deParaListarGrupos(tenantId: number): Promise<{ grupoNome: string; subgrupoNome: string | null }[]> {
    const { data } = await api.get(`${BASE}/depara/grupos`, { params: { tenantId } });
    return Array.isArray(data) ? data : [];
  },

  async deParaSalvar(dto: {
    tenantId: number;
    contaId: number;
    contaCodigo: string;
    contaDescricao: string;
    grupoNome: string;
    subgrupoNome?: string;
    justificativaSox: string;
    criadoPorId: number;
  }): Promise<void> {
    await api.post(`${BASE}/depara`, dto);
  },

  async deParaRemover(grupoContaId: number): Promise<void> {
    await api.delete(`${BASE}/depara/${grupoContaId}`);
  },

  async getPeriodosEmpresa(empresaId: number): Promise<{ esteiraId: number; periodoInicio: string; periodoFim: string; ativo: boolean; label: string }[]> {
    const { data } = await api.get(`${BASE}/matriz/periodos`, { params: { empresaId } });
    return data;
  },

  async deParaSalvarEmLote(dto: {
    tenantId: number;
    contaIds: number[];
    grupoNome: string;
    subgrupoNome?: string;
    justificativaSox: string;
    criadoPorId: number;
    tipoGrupo?: 'CLIENTES';
  }): Promise<{ mapeadas: number }> {
    const { data } = await api.post(`${BASE}/depara/bulk`, dto);
    return data;
  },

  // Ação explícita do usuário: aplica a regra de Depreciação/Amortização (a sincronização do ECD não vincula nada).
  async deParaPreVincular(tenantId: number): Promise<{ preVinculadas: number }> {
    const { data } = await api.post(`${BASE}/depara/pre-vincular`, null, { params: { tenantId } });
    return data;
  },

  // Remove só os vínculos criados pela regra; mapeamentos feitos por pessoas não são tocados.
  async deParaDesfazerPreVinculos(tenantId: number): Promise<{ removidas: number }> {
    const { data } = await api.delete(`${BASE}/depara/pre-vinculos`, { params: { tenantId } });
    return data;
  },

  async deParaRemoverGrupo(tenantId: number, grupoNome: string): Promise<{ removidas: number }> {
    const { data } = await api.delete(`${BASE}/depara/grupo`, { params: { tenantId, grupoNome } });
    return data;
  },

  async deParaSalvarComposicao(dto: {
    tenantId: number; grupoNome: string; contaIds: number[];
    justificativaSox: string; criadoPorId: number; tipoGrupo?: 'CLIENTES';
  }): Promise<{ adicionadas: number; removidas: number; total: number }> {
    const { data } = await api.put(`${BASE}/depara/grupo/contas`, dto);
    return data;
  },

  async deParaRenomearGrupo(dto: {
    tenantId: number; grupoNomeAtual: string; grupoNomeNovo: string; justificativaSox: string;
  }): Promise<{ atualizadas: number }> {
    const { data } = await api.put(`${BASE}/depara/grupo`, dto);
    return data;
  },

  async sincronizarEcd(tenantId: number, esteiraId?: number): Promise<{ inseridas: number; atualizadas: number; total: number }> {
    const { data } = await api.post(`${BASE}/matriz/sincronizar-ecd`, null, { params: { tenantId, ...(esteiraId ? { esteiraId } : {}) } });
    return data;
  },
};

export default workflowService;
